import { ReactNode, useMemo, useState } from "react";
import { AlertCircle, Plus, Search } from "lucide-react";
import { DynamicIcon, IconName } from "lucide-react/dynamic";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useAgentList } from "~/hooks/useAgentList";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { AdminPage } from "~/pages/manifold-nextgen/components/admin/AdminPage";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { useDatasetsById } from "~/pages/manifold-nextgen/hooks/useDatasetsById";
import { AgentListView } from "~/services/agents";
import {
  QuickAction,
  QuickActionList,
  QuickActionPlacement,
  quickActionListPath,
} from "~/services/quickActions";
import { CreateQuickActionModal } from "./CreateQuickActionModal";
import { QuickActionDrawer } from "./QuickActionDrawer";
import {
  DEFAULT_ICON,
  PLACEMENT_ICON,
  PLACEMENT_LABEL,
  resolvePlacementTargetName,
} from "./constants";

type StatusFilter = "all" | "enabled" | "disabled";

const matchesSearch = (action: QuickAction, term: string) => {
  const needle = term.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return (
    action.title.toLowerCase().includes(needle) ||
    (action.subtitle?.toLowerCase().includes(needle) ?? false)
  );
};

const matchesStatus = (action: QuickAction, status: StatusFilter) => {
  if (status === "all") {
    return true;
  }
  return status === "enabled" ? action.enabled : !action.enabled;
};

export const QuickActionsAdmin = () => {
  useDocumentTitle("Quick Actions");

  const { data, error, isLoading, mutate } = useFetcher<QuickActionList>(quickActionListPath());
  const { data: agentList } = useAgentList(true, AgentListView.ALL);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<QuickAction | null>(null);

  const headerActions = useMemo(
    () =>
      [
        <Button
          key="new-quick-action"
          size="sm"
          className="text-xs"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="size-4" />
          New Quick Action
        </Button>,
      ] as [ReactNode],
    []
  );
  useNextGenHeader({ actions: headerActions });

  const agentNamesById = useMemo(
    () => new Map((agentList?.items ?? []).map((agent) => [agent.id, agent.display_name])),
    [agentList]
  );
  // The table only names dataset-placed actions, so resolve just those targets
  // rather than reading the whole catalog.
  const placementDatasetIds = useMemo(
    () =>
      data && [
        ...new Set(
          data.items.flatMap((action) =>
            action.placement === QuickActionPlacement.DATASET && action.placement_id
              ? [action.placement_id]
              : []
          )
        ),
      ],
    [data]
  );
  const { data: datasets } = useDatasetsById(placementDatasetIds);
  const datasetNamesById = useMemo(
    () => new Map((datasets ?? []).map((dataset) => [dataset.id, dataset.title])),
    [datasets]
  );
  const agentById = useMemo(
    () => new Map((agentList?.items ?? []).map((agent) => [agent.id, agent])),
    [agentList]
  );
  const datasetById = useMemo(
    () => new Map((datasets ?? []).map((dataset) => [dataset.id, dataset])),
    [datasets]
  );

  const items = data?.items ?? [];
  const filteredActions = useMemo(
    () =>
      items.filter(
        (action) => matchesSearch(action, search) && matchesStatus(action, statusFilter)
      ),
    [items, search, statusFilter]
  );

  return (
    <AdminPage
      title="Quick Actions"
      description="Configure the quick actions available to users across your organization — on agents, datasets, or the homepage."
      width="default"
    >
      <div className="flex items-center gap-3">
        <div className="relative max-w-80 flex-1">
          <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search quick actions"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger aria-label="Status" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="size-4 shrink-0" />
          <span>Unable to load quick actions</span>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-11" />
              <TableHead>Title</TableHead>
              <TableHead>Placement</TableHead>
              <TableHead>Project Name</TableHead>
              <TableHead className="w-24">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow className="pointer-events-none">
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : filteredActions.length === 0 ? (
              <TableRow className="pointer-events-none">
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  No quick actions found{search && ` matching "${search}"`}
                </TableCell>
              </TableRow>
            ) : (
              filteredActions.map((action) => {
                const PlacementIcon = PLACEMENT_ICON[action.placement];
                const placementTargetName = resolvePlacementTargetName(
                  action.placement,
                  action.placement_id,
                  agentNamesById,
                  datasetNamesById
                );
                const placementAgent =
                  action.placement === QuickActionPlacement.AGENT && action.placement_id
                    ? agentById.get(action.placement_id)
                    : undefined;
                const placementDataset =
                  action.placement === QuickActionPlacement.DATASET && action.placement_id
                    ? datasetById.get(action.placement_id)
                    : undefined;
                return (
                  <TableRow
                    key={action.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedAction(action)}
                  >
                    <TableCell>
                      <div className="flex size-8 items-center justify-center rounded-md bg-primary/10">
                        <DynamicIcon
                          name={(action.icon ?? DEFAULT_ICON) as IconName}
                          className="size-4 text-primary"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-foreground">{action.title}</div>
                      {action.subtitle && (
                        <div className="text-sm text-muted-foreground">{action.subtitle}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex min-w-0 items-center gap-1.5 text-sm">
                        {placementTargetName ? (
                          <>
                            {placementAgent && <AgentAvatar agent={placementAgent} size={16} />}
                            {placementDataset && (
                              <DatasetIcon dataset={placementDataset} size={16} />
                            )}
                            <span className="truncate text-foreground">{placementTargetName}</span>
                          </>
                        ) : (
                          <>
                            <PlacementIcon className="size-3.5 shrink-0 text-black dark:text-white" />
                            <span>{PLACEMENT_LABEL[action.placement]}</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{action.project_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={action.enabled ? "success" : "muted"}
                          className="gap-1 rounded-full"
                        >
                          <span className="size-1.5 shrink-0 rounded-full bg-current" />
                          {action.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <CreateQuickActionModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => mutate()}
      />
      {selectedAction && (
        <QuickActionDrawer
          quickAction={selectedAction}
          open={!!selectedAction}
          onClose={() => setSelectedAction(null)}
          onSaved={() => mutate()}
          onDeleted={() => mutate()}
        />
      )}
    </AdminPage>
  );
};
